/*
 * Copyright (c) 2024 Bolmate B.V.
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {ResponseData} from '@dvrd/dvr-controls';
import PlatformUsage from './platformUsage';
import {onSetCrumbs} from '../../../util/events';
import {
    clearAnalyticsData,
    getAnalyticsSettings,
    getPlatformUsage,
    GetPlatformUsageParams,
    runAnalyticsAggregation,
    updateAnalyticsSettings
} from '../../../util/requestSender';
import {ADMIN_PLATFORM_USAGE_ROUTE, ADMIN_ROUTE} from '../../../util/constants';
import {setAnalyticsCollectionEnabled} from '../../../util/analytics';
import {showDialog} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

export interface EmailStats {
    // All accounts
    email_tracking_enabled: number;
    email_tracking_disabled: number;
    auto_mail_enabled: number;
    auto_mail_disabled: number;
    invoice_only: number;
    invoice_and_review: number;
    // Active accounts only (paying, trial, free)
    email_tracking_enabled_active: number;
    email_tracking_disabled_active: number;
    auto_mail_enabled_active: number;
    auto_mail_disabled_active: number;
    invoice_only_active: number;
    invoice_and_review_active: number;
}

export interface NotificationStats {
    by_type: {
        REVIEWS: number;
        BUY_BOX: number;
        STOCK: number;
        ORDER: number;
    };
    by_type_active: {
        REVIEWS: number;
        BUY_BOX: number;
        STOCK: number;
        ORDER: number;
    };
    module_paid: number;
    module_trial: number;
    module_total: number;
}

export interface AdditionalUsersStats {
    // All accounts
    accounts_with_additional_users: number;
    total_additional_users: number;
    // Active accounts only
    accounts_with_additional_users_active: number;
    total_additional_users_active: number;
}

export interface TimeseriesPoint {
    period: string;
    value: number;
}

export interface TimeseriesSeries {
    seriesKey: string;
    points: TimeseriesPoint[];
}

export interface FeatureDrilldown {
    page_views: TimeseriesPoint[];
    actions: TimeseriesPoint[];
    conversion: TimeseriesPoint[];
}

export interface TopTableItem {
    key: string;
    title: string;
    group: string;
    views_total: number;
    views_unique: number;
    actions_total: number;
    actions_unique: number;
    conversion_pct: number;
}

export interface AnalyticsFilters {
    from_date: string;
    to_date: string;
    bucket: string;
    metric: string;
    event_name: string;
    breakdown: string;
    feature_group: string | null;
    feature_key: string | null;
    user_search: string | null;
}

export interface AnalyticsSettings {
    collection_enabled: boolean;
    version: number;
    data_counts: {
        raw_events: number;
        user_day_records: number;
        daily_aggregates: number;
    };
    date_ranges: {
        raw_events: {oldest: string | null; newest: string | null};
        user_day_records: {oldest: string | null; newest: string | null};
        daily_aggregates: {oldest: string | null; newest: string | null};
    };
    retention_policy: {
        raw_events_days: number;
        user_day_records_days: number;
        daily_aggregates_days: number | null; // null = indefinite
        aggregation_delay_minutes: number;
    };
    limits: {
        max_raw_events_days: number;
        max_user_day_days: number;
    };
}

export interface EditableSettingsState {
    collection_enabled: boolean;
    raw_events_retention_days: number;
    user_day_retention_days: number;
    daily_aggregates_retention_days: number; // 0 = indefinite
    aggregation_delay_minutes: number;
}

export interface AggregationCooldown {
    can_run: boolean;
    cooldown_remaining_seconds: number;
    cooldown_total_seconds: number;
    last_run_at: string | null;
    delay_minutes: number;
    ready_events_count: number;
    pending_events_count: number;
    pending_ready_in_seconds: number;
}

export interface PlatformUsageData {
    // Snapshot stats
    email_stats: EmailStats;
    notification_stats: NotificationStats;
    additional_users_stats: AdditionalUsersStats;
    // Analytics (optional - may not be present if tables don't exist)
    has_any_data?: boolean;
    aggregation_delay_minutes?: number;
    aggregation_cooldown?: AggregationCooldown;
    timeseries_global?: TimeseriesPoint[];
    timeseries_top?: TimeseriesSeries[];
    timeseries_user_growth?: TimeseriesPoint[];
    feature_drilldown?: FeatureDrilldown | null;
    top_table?: TopTableItem[];
    filters?: AnalyticsFilters | null;
}

export type BucketOption = 'auto' | 'day' | 'week' | 'month';
export type MetricOption = 'total_events' | 'unique_users';
export type EventNameOption = 'page_view' | 'action_success' | 'action_failed';
export type BreakdownOption = 'feature_group' | 'feature_key';

export interface FilterState {
    fromDate: string;
    toDate: string;
    bucket: BucketOption;
    metric: MetricOption;
    eventName: EventNameOption;
    topN: number;
    breakdown: BreakdownOption;
    featureKey: string | null;
    featureGroup: string | null;
    userSearch: string;
    // User type filters
    includeRegularUsers: boolean;
    includeAdmins: boolean;
    includeAdminAsUser: boolean;
}

function getDefaultDateRange(): {fromDate: string; toDate: string} {
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 30);
    return {
        fromDate: fromDate.toISOString().split('T')[0],
        toDate: toDate.toISOString().split('T')[0]
    };
}

export default function PlatformUsageController() {
    const {t} = useTranslation();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<PlatformUsageData | null>(null);
    const initialLoadDone = useRef(false);
    const requestIdRef = useRef(0); // Track request ID to ignore stale responses
    const {fromDate: defaultFromDate, toDate: defaultToDate} = getDefaultDateRange();

    // Analytics settings state
    const [analyticsSettings, setAnalyticsSettings] = useState<AnalyticsSettings | null>(null);
    const [editableSettings, setEditableSettings] = useState<EditableSettingsState | null>(null);
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [settingsSaving, setSettingsSaving] = useState(false);
    const [settingsHasChanges, setSettingsHasChanges] = useState(false);
    const [clearDataModalActive, setClearDataModalActive] = useState(false);
    const [clearDataConfirmed, setClearDataConfirmed] = useState(false);
    const [clearDataLoading, setClearDataLoading] = useState(false);

    // Cooldown countdown state
    const [cooldownRemainingSeconds, setCooldownRemainingSeconds] = useState(0);

    const [filters, setFilters] = useState<FilterState>({
        fromDate: defaultFromDate,
        toDate: defaultToDate,
        bucket: 'auto',
        metric: 'unique_users',
        eventName: 'page_view',
        topN: 5,
        breakdown: 'feature_group',
        featureKey: null,
        featureGroup: null,
        userSearch: '',
        // Default: no checkboxes selected = show ALL users (no filtering)
        includeRegularUsers: true,
        includeAdmins: false,
        includeAdminAsUser: false
    });

    const fetchPlatformUsageData = useCallback(() => {
        // Don't fetch if date range is invalid (fromDate > toDate)
        if (filters.fromDate > filters.toDate) {
            setLoading(false);
            return;
        }

        // Increment request ID and capture it for this request
        const currentRequestId = ++requestIdRef.current;

        const params: GetPlatformUsageParams = {
            from_date: filters.fromDate,
            to_date: filters.toDate,
            bucket: filters.bucket,
            metric: filters.metric,
            event_name: filters.eventName,
            top_n: filters.topN,
            breakdown: filters.breakdown,
            feature_key: filters.featureKey ?? undefined,
            feature_group: filters.featureGroup ?? undefined,
            user_search: filters.userSearch || undefined,
            include_regular_users: filters.includeRegularUsers,
            include_admins: filters.includeAdmins,
            include_admin_as_user: filters.includeAdminAsUser
        };

        let requestCompleted = false;

        // Safety timeout - if request doesn't complete in 30 seconds, stop loading
        const timeoutId = setTimeout(() => {
            if (!requestCompleted && currentRequestId === requestIdRef.current) {
                setLoading(false);
                showDialog(t('admin.platformUsage.timeout', 'Het verzoek duurde te lang. Probeer het opnieuw.'));
            }
        }, 30000);

        const request = getPlatformUsage({
            data: params,
            callback: (response: ResponseData) => {
                requestCompleted = true;
                clearTimeout(timeoutId);
                // Ignore stale responses from previous requests
                if (currentRequestId !== requestIdRef.current) {
                    return;
                }
                setLoading(false);
                if (response.success) {
                    setData({
                        email_stats: response.email_stats,
                        notification_stats: response.notification_stats,
                        additional_users_stats: response.additional_users_stats,
                        has_any_data: response.has_any_data,
                        aggregation_delay_minutes: response.aggregation_delay_minutes,
                        aggregation_cooldown: response.aggregation_cooldown,
                        timeseries_global: response.timeseries_global,
                        timeseries_top: response.timeseries_top,
                        timeseries_user_growth: response.timeseries_user_growth,
                        feature_drilldown: response.feature_drilldown,
                        top_table: response.top_table,
                        filters: response.filters
                    });
                } else {
                    showDialog(
                        response.message ??
                            t('admin.platformUsage.fetchFailed', 'Het ophalen van de statistieken is niet gelukt.')
                    );
                }
            },
            errorCallback: () => {
                requestCompleted = true;
                clearTimeout(timeoutId);
                // Ignore stale error responses from previous requests
                if (currentRequestId !== requestIdRef.current) {
                    return;
                }
                setLoading(false);
                showDialog(
                    t(
                        'admin.platformUsage.fetchError',
                        'Er is een fout opgetreden bij het ophalen van de statistieken.'
                    )
                );
            }
        });

        // Handle case where request is null (e.g., maintenance mode)
        if (!request) {
            clearTimeout(timeoutId);
            setLoading(false);
        }
    }, [filters]);

    const loadData = useCallback(
        (runAggregationFirst = false) => {
            setLoading(true);

            if (runAggregationFirst) {
                runAnalyticsAggregation({
                    callback: () => {
                        fetchPlatformUsageData();
                    }
                });
            } else {
                fetchPlatformUsageData();
            }
        },
        [fetchPlatformUsageData]
    );

    // Calculate if date range allows daily bucket (max 31 days)
    const dateRangeDays = useMemo(() => {
        const from = new Date(filters.fromDate);
        const to = new Date(filters.toDate);
        const diffTime = to.getTime() - from.getTime();
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }, [filters.fromDate, filters.toDate]);

    const isDayBucketAllowed = dateRangeDays <= 31;

    // Auto-switch from 'day' to 'auto' when date range exceeds 31 days
    useEffect(() => {
        if (!isDayBucketAllowed && filters.bucket === 'day') {
            setFilters(prev => ({...prev, bucket: 'auto'}));
        }
    }, [isDayBucketAllowed, filters.bucket]);

    function updateFilter<K extends keyof FilterState>(key: K, value: FilterState[K]) {
        setFilters(prev => ({...prev, [key]: value}));
    }

    function onDrillDown(featureKey: string) {
        setFilters(prev => ({
            ...prev,
            featureKey,
            breakdown: 'feature_key' as BreakdownOption
        }));
    }

    function onClearDrillDown() {
        setFilters(prev => ({
            ...prev,
            featureKey: null,
            breakdown: 'feature_group' as BreakdownOption
        }));
    }

    function onResetFilters() {
        const {fromDate, toDate} = getDefaultDateRange();
        setFilters({
            fromDate,
            toDate,
            bucket: 'auto',
            metric: 'unique_users',
            eventName: 'page_view',
            topN: 5,
            breakdown: 'feature_group',
            featureKey: null,
            featureGroup: null,
            userSearch: '',
            // Reset to initial state: show all users by default
            includeRegularUsers: false,
            includeAdmins: false,
            includeAdminAsUser: false
        });
    }

    function onRunAggregation() {
        setLoading(true);
        runAnalyticsAggregation({
            callback: response => {
                if (response.success) {
                    showDialog(
                        `${t('admin.platformUsage.aggregationDonePrefix', 'Aggregatie voltooid')}: ${response.processed ?? 0} ${t('admin.platformUsage.eventsProcessed', 'events verwerkt.')}`
                    );
                    fetchPlatformUsageData(); // Refresh data after aggregation (includes new cooldown status)
                } else {
                    setLoading(false);
                    showDialog(
                        response.message ?? t('admin.platformUsage.aggregationFailed', 'Aggregatie is mislukt.')
                    );
                }
            }
        });
    }

    // Analytics settings handlers
    const fetchAnalyticsSettings = useCallback(() => {
        setSettingsLoading(true);
        getAnalyticsSettings({
            callback: (response: ResponseData) => {
                setSettingsLoading(false);
                if (response.success) {
                    const settings: AnalyticsSettings = {
                        collection_enabled: response.collection_enabled,
                        version: response.version,
                        data_counts: response.data_counts,
                        date_ranges: response.date_ranges,
                        retention_policy: response.retention_policy,
                        limits: response.limits
                    };
                    setAnalyticsSettings(settings);
                    // Update global analytics collection flag
                    setAnalyticsCollectionEnabled(settings.collection_enabled);
                    // Initialize editable settings from server values
                    setEditableSettings({
                        collection_enabled: settings.collection_enabled,
                        raw_events_retention_days: settings.retention_policy.raw_events_days,
                        user_day_retention_days: settings.retention_policy.user_day_records_days,
                        daily_aggregates_retention_days: settings.retention_policy.daily_aggregates_days ?? 0,
                        aggregation_delay_minutes: settings.retention_policy.aggregation_delay_minutes
                    });
                    setSettingsHasChanges(false);
                }
            }
        });
    }, []);

    function onUpdateEditableSetting<K extends keyof EditableSettingsState>(key: K, value: EditableSettingsState[K]) {
        setEditableSettings(prev => {
            if (!prev) return prev;
            const newSettings = {...prev, [key]: value};
            // Check if there are changes compared to server state
            if (analyticsSettings) {
                const hasChanges =
                    newSettings.collection_enabled !== analyticsSettings.collection_enabled ||
                    newSettings.raw_events_retention_days !== analyticsSettings.retention_policy.raw_events_days ||
                    newSettings.user_day_retention_days !== analyticsSettings.retention_policy.user_day_records_days ||
                    newSettings.daily_aggregates_retention_days !==
                        (analyticsSettings.retention_policy.daily_aggregates_days ?? 0) ||
                    newSettings.aggregation_delay_minutes !==
                        analyticsSettings.retention_policy.aggregation_delay_minutes;
                setSettingsHasChanges(hasChanges);
            }
            return newSettings;
        });
    }

    function onSaveSettings() {
        if (!analyticsSettings || !editableSettings) return;

        setSettingsSaving(true);
        updateAnalyticsSettings({
            data: {
                version: analyticsSettings.version,
                collection_enabled: editableSettings.collection_enabled,
                raw_events_retention_days: editableSettings.raw_events_retention_days,
                user_day_retention_days: editableSettings.user_day_retention_days,
                daily_aggregates_retention_days: editableSettings.daily_aggregates_retention_days,
                aggregation_delay_minutes: editableSettings.aggregation_delay_minutes
            },
            callback: (response: ResponseData) => {
                setSettingsSaving(false);
                if (response.success) {
                    showDialog(t('admin.platformUsage.settingsSaved', 'Instellingen opgeslagen.'));
                    // Update global analytics collection flag immediately
                    setAnalyticsCollectionEnabled(response.collection_enabled);
                    // Update local state with new data from server
                    const settings: AnalyticsSettings = {
                        collection_enabled: response.collection_enabled,
                        version: response.version,
                        data_counts: response.data_counts,
                        date_ranges: response.date_ranges,
                        retention_policy: response.retention_policy,
                        limits: response.limits
                    };
                    setAnalyticsSettings(settings);
                    setEditableSettings({
                        collection_enabled: settings.collection_enabled,
                        raw_events_retention_days: settings.retention_policy.raw_events_days,
                        user_day_retention_days: settings.retention_policy.user_day_records_days,
                        daily_aggregates_retention_days: settings.retention_policy.daily_aggregates_days ?? 0,
                        aggregation_delay_minutes: settings.retention_policy.aggregation_delay_minutes
                    });
                    setSettingsHasChanges(false);
                    // Refresh data to update cooldown status with new aggregation_delay
                    fetchPlatformUsageData();
                } else if (response.conflict) {
                    // Version conflict - refresh settings
                    showDialog(
                        response.message ??
                            t(
                                'admin.platformUsage.settingsConflict',
                                'De instellingen zijn ondertussen door een andere beheerder gewijzigd.'
                            )
                    );
                    fetchAnalyticsSettings();
                } else {
                    showDialog(
                        response.message ??
                            t('admin.platformUsage.settingsSaveFailed', 'Het opslaan van de instellingen is mislukt.')
                    );
                }
            }
        });
    }

    function onOpenClearDataModal() {
        setClearDataModalActive(true);
        setClearDataConfirmed(false);
    }

    function onCloseClearDataModal() {
        setClearDataModalActive(false);
        setClearDataConfirmed(false);
    }

    function onClearAllData() {
        setClearDataLoading(true);
        clearAnalyticsData({
            callback: (response: ResponseData) => {
                setClearDataLoading(false);
                if (response.success) {
                    onCloseClearDataModal();
                    showDialog(t('admin.platformUsage.dataCleared', 'Alle analytics data is verwijderd.'));
                    // Refresh both settings and main data
                    fetchAnalyticsSettings();
                    loadData();
                } else {
                    showDialog(
                        response.message ??
                            t('admin.platformUsage.clearDataFailed', 'Het verwijderen van analytics data is mislukt.')
                    );
                }
            }
        });
    }

    useEffect(() => {
        onSetCrumbs([
            {label: t('admin.platformUsage.crumb.admin', 'Admin'), route: ADMIN_ROUTE},
            {label: t('admin.platformUsage.crumb.platformUsage', 'Platformgebruik'), route: ADMIN_PLATFORM_USAGE_ROUTE}
        ]);
        // Run aggregation on initial load to ensure latest events are in charts
        loadData(true);
        // Load analytics settings
        fetchAnalyticsSettings();
    }, []);

    // Check if date range is valid
    const isDateRangeValid = filters.fromDate <= filters.toDate;

    // Reload data when filters change (with debounce for text inputs)
    useEffect(() => {
        // Skip the initial render - data is already loaded in the mount effect
        if (!initialLoadDone.current) {
            initialLoadDone.current = true;
            return;
        }

        // Don't load data if date range is invalid
        if (!isDateRangeValid) {
            setLoading(false);
            return;
        }

        const timeoutId = setTimeout(() => {
            loadData();
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [loadData, isDateRangeValid]);

    // Sync cooldown from server data
    useEffect(() => {
        const serverCooldown = data?.aggregation_cooldown?.cooldown_remaining_seconds ?? 0;
        setCooldownRemainingSeconds(serverCooldown);
    }, [data?.aggregation_cooldown?.cooldown_remaining_seconds]);

    // Countdown timer for cooldown
    useEffect(() => {
        if (cooldownRemainingSeconds <= 0) return;

        const intervalId = setInterval(() => {
            setCooldownRemainingSeconds(prev => {
                if (prev <= 1) {
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(intervalId);
    }, [cooldownRemainingSeconds]);

    return (
        <PlatformUsage
            loading={loading}
            data={data}
            filters={filters}
            isDayBucketAllowed={isDayBucketAllowed}
            onUpdateFilter={updateFilter}
            onDrillDown={onDrillDown}
            onClearDrillDown={onClearDrillDown}
            onResetFilters={onResetFilters}
            onRefresh={() => loadData()}
            onRunAggregation={onRunAggregation}
            cooldownRemainingSeconds={cooldownRemainingSeconds}
            // Analytics settings props
            analyticsSettings={analyticsSettings}
            editableSettings={editableSettings}
            settingsLoading={settingsLoading}
            settingsSaving={settingsSaving}
            settingsHasChanges={settingsHasChanges}
            clearDataModalActive={clearDataModalActive}
            clearDataConfirmed={clearDataConfirmed}
            clearDataLoading={clearDataLoading}
            onUpdateEditableSetting={onUpdateEditableSetting}
            onSaveSettings={onSaveSettings}
            onOpenClearDataModal={onOpenClearDataModal}
            onCloseClearDataModal={onCloseClearDataModal}
            onSetClearDataConfirmed={setClearDataConfirmed}
            onClearAllData={onClearAllData}
        />
    );
}
